export interface FactLike {
    id: string;
    factType: string;
    factValue: Record<string, unknown>;
    confidenceScore?: string;
    lastVerifiedAt?: string | null;
    createdAt?: string;
}

export const SUBJECT_KEY_FACT_TYPES = [
    'known_loan_amount',
    'financial_obligations',
    'last_field_report',
] as const;

export const POI_KEY_FACT_TYPES = [
    'verification_status',
    'claimed_location',
    'financial_exposure',
    'last_field_report',
] as const;

/** Operational facts first; source pointer last. */
export const FACT_DISPLAY_ORDER = [
    'known_loan_amount',
    'financial_obligations',
    'financial_exposure',
    'contact_profile',
    'business_operations',
    'operational_profile',
    'claimed_location',
    'verification_status',
    'claimed_assets',
    'last_field_report',
] as const;

export function findFact<T extends FactLike>(facts: T[], factType: string): T | undefined {
    return facts.find((fact) => fact.factType === factType);
}

export function formatFactWhen(value?: string | null): string | null {
    return value ? new Date(value).toLocaleString() : null;
}

export function sortFactsForDisplay<T extends FactLike>(facts: T[]): T[] {
    const orderIndex = new Map(FACT_DISPLAY_ORDER.map((type, index) => [type, index]));
    return [...facts].sort((a, b) => {
        const aIndex = orderIndex.get(a.factType as (typeof FACT_DISPLAY_ORDER)[number]) ?? 999;
        const bIndex = orderIndex.get(b.factType as (typeof FACT_DISPLAY_ORDER)[number]) ?? 999;
        if (aIndex !== bIndex) {
            return aIndex - bIndex;
        }
        return a.factType.localeCompare(b.factType);
    });
}

export function formatMoney(amount: unknown, currency?: unknown): string | null {
    if (amount === null || amount === undefined || amount === '') {
        return null;
    }
    const numeric = typeof amount === 'number' ? amount : Number(amount);
    if (!Number.isFinite(numeric)) {
        return String(amount);
    }
    const code = typeof currency === 'string' && currency.trim() ? currency : 'NGN';
    try {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: code,
            maximumFractionDigits: 0,
        }).format(numeric);
    } catch {
        return `${numeric.toLocaleString()} ${code}`;
    }
}

export function formatScalar(value: unknown): string | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }
    if (typeof value === 'number' || typeof value === 'boolean') {
        return String(value);
    }
    if (typeof value === 'string') {
        return value.trim() || null;
    }
    return JSON.stringify(value);
}

export function formatAddressLine(value: Record<string, unknown>): string | null {
    const parts = [
        value.address ?? value.street,
        value.city,
        value.lga,
        value.state,
        value.landmark,
    ]
        .map(formatScalar)
        .filter(Boolean);
    return parts.length > 0 ? parts.join(', ') : null;
}
